import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, MessageCircle, Printer, XCircle } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import { apiGet, apiPost } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatusPill } from '@/components/ui/Pill';
import { ErrorState, LoadingState } from '@/components/ui/States';
import type { BillFull } from '@/types/api';
import { fmtDateTime, fmtPhone } from '@/utils/format';

/**
 * Full bill detail. Reads the canonical state from the same endpoint
 * the Flutter app uses, so a CA reviewing on desktop and the cashier
 * reviewing on mobile see exactly the same numbers. Three actions
 * surfaced in the header — PDF (synchronous via /pdf), WhatsApp share,
 * and Cancel — plus print which uses the browser's `window.print()`.
 */
export function BillDetailPage() {
  const { id } = useParams();
  const billId = Number(id);
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const query = useQuery({
    queryKey: ['bill', billId],
    queryFn: () => apiGet<BillFull>(endpoints.bill(billId)),
    enabled: Number.isFinite(billId),
  });

  const cancelMut = useMutation({
    mutationFn: (reason: string) =>
      apiPost(endpoints.billCancel(billId), { reason }),
    onSuccess: () => {
      toast.success('Bill cancelled');
      setCancelOpen(false);
      queryClient.invalidateQueries({ queryKey: ['bill', billId] });
      queryClient.invalidateQueries({ queryKey: ['bills'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const pdfMut = useMutation({
    mutationFn: () => apiGet<{ pdf_url: string; phone?: string; message?: string }>(
      endpoints.billPdf(billId),
    ),
    onSuccess: (data) => {
      if (data.pdf_url) window.open(data.pdf_url, '_blank', 'noopener');
      else toast.error('PDF unavailable');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const whatsappMut = useMutation({
    mutationFn: () => apiGet<{ pdf_url: string; phone: string; message: string }>(
      endpoints.billPdf(billId),
    ),
    onSuccess: (d) => {
      const phone = (d.phone || '').replace(/\D/g, '');
      if (!phone) {
        toast.error('No customer phone on this bill');
        return;
      }
      const text = encodeURIComponent(d.message || `Bill ${query.data?.bill_number}`);
      window.open(`https://wa.me/${phone}?text=${text}`, '_blank', 'noopener');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!Number.isFinite(billId)) {
    return <ErrorState title="Invalid bill id" />;
  }
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Couldn't load bill"
        description={(query.error as Error)?.message}
        onRetry={() => query.refetch()}
      />
    );
  }

  const b = query.data;
  const items = b.items ?? [];
  const payments = b.payments ?? [];
  const subtotal = items.reduce(
    (s, i) => s + i.quantity * i.unit_price - (i.discount_amount ?? 0),
    0,
  );
  const paid = (payments ?? []).reduce((s, p) => s + p.amount, 0);
  const due = Math.max(0, b.total - paid);

  return (
    <>
      <PageHeader
        back
        title={b.bill_number}
        description={`Created ${fmtDateTime(b.created_at)}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              iconLeft={<Printer className="h-4 w-4" />}
              onClick={() => window.print()}
            >
              Print
            </Button>
            <Button
              variant="outline"
              iconLeft={<Download className="h-4 w-4" />}
              loading={pdfMut.isPending}
              onClick={() => pdfMut.mutate()}
            >
              PDF
            </Button>
            <Button
              variant="success"
              iconLeft={<MessageCircle className="h-4 w-4" />}
              loading={whatsappMut.isPending}
              onClick={() => whatsappMut.mutate()}
            >
              WhatsApp
            </Button>
            {b.status !== 'cancelled' && (
              <Button
                variant="danger"
                iconLeft={<XCircle className="h-4 w-4" />}
                onClick={() => setCancelOpen(true)}
              >
                Cancel
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Card>
          <SectionHeader title="Customer" />
          <div className="space-y-1 text-sm">
            <div className="font-medium text-base">
              {b.customer?.name || b.customer_name_snapshot || 'Walk-in customer'}
            </div>
            <div className="text-ink-soft">
              {fmtPhone(b.customer?.phone || b.customer_phone_snapshot)}
            </div>
            <div className="pt-3">
              <StatusPill status={b.status} />
            </div>
            {b.due_date && (
              <div className="text-xs text-ink-soft pt-2">
                Due date: {fmtDateTime(b.due_date)}
              </div>
            )}
          </div>
        </Card>

        <Card>
          <SectionHeader title="Totals" />
          <dl className="grid grid-cols-[1fr_auto] gap-y-2 text-sm">
            <dt className="text-ink-soft">Subtotal</dt>
            <dd className="text-right"><Money value={subtotal} /></dd>
            <dt className="text-ink-soft">Discount</dt>
            <dd className="text-right"><Money value={-(b.discount_amount ?? 0)} /></dd>
            <dt className="font-semibold pt-2 border-t border-line">Total</dt>
            <dd className="text-right pt-2 border-t border-line">
              <Money value={b.total} className="font-bold text-base" />
            </dd>
            <dt className="text-success">Paid</dt>
            <dd className="text-right text-success"><Money value={paid} /></dd>
            <dt className={due > 0 ? 'text-danger font-semibold' : 'text-ink-soft'}>
              {due > 0 ? 'Due' : 'Settled'}
            </dt>
            <dd className={`text-right ${due > 0 ? 'text-danger font-semibold' : ''}`}>
              <Money value={due} />
            </dd>
          </dl>
        </Card>

        <Card>
          <SectionHeader title="Sold by" />
          <div className="text-sm">
            <div className="font-medium">
              {b.sold_by?.name ?? 'Owner'}
            </div>
            <div className="text-ink-soft text-xs mt-1">
              {b.bill_type === 'gst' ? 'GST invoice' : 'Non-GST bill'}
            </div>
          </div>
        </Card>
      </div>

      {/* Items */}
      <Card padded={false}>
        <div className="px-5 py-4 border-b border-line">
          <h2 className="text-base font-semibold text-ink">Items ({items.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2">
              <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
                <th className="px-5 py-2">Item</th>
                <th className="px-5 py-2 text-right">Qty</th>
                <th className="px-5 py-2 text-right hidden md:table-cell">Unit price</th>
                <th className="px-5 py-2 text-right hidden md:table-cell">Discount</th>
                <th className="px-5 py-2 text-right">Line total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const line = it.quantity * it.unit_price - (it.discount_amount ?? 0);
                return (
                  <tr key={it.id} className="border-t border-line">
                    <td className="px-5 py-3">
                      <div className="font-medium">
                        {it.product?.name ?? 'Item'}
                      </div>
                      {it.product?.hsn_code && (
                        <div className="text-xs text-ink-soft">
                          HSN {it.product.hsn_code}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {it.quantity}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums hidden md:table-cell">
                      <Money value={it.unit_price} />
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums hidden md:table-cell">
                      <Money value={-(it.discount_amount ?? 0)} />
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">
                      <Money value={line} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Payments */}
      {payments.length > 0 && (
        <Card padded={false} className="mt-4">
          <div className="px-5 py-4 border-b border-line">
            <h2 className="text-base font-semibold text-ink">Payments</h2>
          </div>
          <ul className="divide-y divide-line">
            {payments.map((p, i) => (
              <li
                key={p.id ?? i}
                className="flex items-center gap-3 px-5 py-3"
              >
                <div className="h-8 w-8 grid place-items-center rounded-lg bg-success/10 text-success uppercase text-xs font-bold">
                  {p.mode.slice(0, 3)}
                </div>
                <div className="flex-1 text-sm">
                  <div className="font-medium capitalize">{p.mode}</div>
                  {p.reference_number && (
                    <div className="text-xs text-ink-soft">
                      Ref {p.reference_number}
                    </div>
                  )}
                </div>
                <Money value={p.amount} className="font-semibold" />
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Cancel modal */}
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={`Cancel ${b.bill_number}?`}
        description="Cancelled bills stay in the books but are excluded from totals."
        footer={
          <>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Keep
            </Button>
            <Button
              variant="danger"
              loading={cancelMut.isPending}
              onClick={() => cancelMut.mutate(cancelReason)}
            >
              Cancel bill
            </Button>
          </>
        }
      >
        <label className="block text-sm font-medium text-ink mb-2">
          Reason (optional)
        </label>
        <textarea
          rows={3}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Returned by customer, wrong amount, etc."
          className="w-full rounded-xl bg-surface border border-line p-3 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none"
        />
      </Modal>
    </>
  );
}
